import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { email as emailSchema, magicLinkRedirect, type MagicLinkRedirect } from '@gg/shared';
import { Button, MailIcon, Notice, PageHeader, TextField } from '@/components/ui';
import { LanguageSwitch } from '@/components/LanguageSwitch';
import { Turnstile } from '@/components/Turnstile';
import { funnel } from '@/lib/analytics';
import { useEvent, useRequestMagicLink } from '@/lib/queries';

const schema = z.object({ email: emailSchema });
const RESEND_DELAY_S = 60;

/** Screen 05: magic-link login (P-09), with the "link sent" state and a 60 s resend delay. */
export function Login() {
  const { t } = useTranslation();
  const { data: event } = useEvent();
  // The back office sends organisers here with ?redirect=/admin, the MCP consent page with its
  // own path (issue #47); anything else lands on /me.
  const asked = magicLinkRedirect.safeParse(useSearchParams()[0].get('redirect'));
  const redirectTo: MagicLinkRedirect = asked.success ? asked.data : '/me';
  const oauth = redirectTo.startsWith('/oauth/');
  const request = useRequestMagicLink();
  const [token, setToken] = useState('');
  const [turnstileKey, setTurnstileKey] = useState(0);
  const onToken = useCallback((tok: string) => setToken(tok), []);
  const [sentAt, setSentAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
  });

  const wait = sentAt ? Math.max(0, RESEND_DELAY_S - Math.floor((now - sentAt) / 1000)) : 0;
  useEffect(() => {
    if (wait === 0) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [wait]);

  const onSubmit = form.handleSubmit(async (v) => {
    funnel.loginRequested();
    try {
      await request.mutateAsync({ email: v.email, turnstileToken: token, redirectTo });
      const at = Date.now();
      setSentAt(at);
      setNow(at);
    } catch {
      // Shown through request.isError.
    } finally {
      // Turnstile tokens are single-use: a resend needs a fresh one.
      setToken('');
      setTurnstileKey((k) => k + 1);
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-1 flex-col pt-14">
      <PageHeader
        eyebrow={event && `${event.name} ${event.edition}`}
        title={t('login.title')}
        intro={t('login.intro')}
      />

      <Turnstile key={turnstileKey} onToken={onToken} />
      <div className="flex flex-1 flex-col gap-5 pt-7">
        <TextField
          size="lg"
          type="email"
          inputMode="email"
          autoComplete="email"
          label={t('login.email')}
          placeholder={t('register.emailPlaceholder')}
          error={form.formState.errors.email && t('register.emailInvalid')}
          {...form.register('email')}
        />
        <Button type="submit" loading={request.isPending} disabled={!token || wait > 0}>
          {t('login.send')}
        </Button>
        {oauth && <p className="m-0 text-sm text-ink-muted">{t('login.sameBrowser')}</p>}

        {request.isError && (
          <p role="alert" className="text-sm text-danger">
            {t('error.generic')}
          </p>
        )}
        {sentAt && (
          <Notice
            title={t('login.sentTitle')}
            icon={<MailIcon size={22} className="text-accent" />}
          >
            <span role="status">
              {t('login.sentBody')}{' '}
              {wait > 0 ? t('login.resendIn', { count: wait }) : t('login.resendNow')}
            </span>
          </Notice>
        )}

        <div className="mt-auto flex flex-col gap-2.5 pb-8 text-center text-sm text-ink-muted">
          <span>
            {t('login.notRegistered')}{' '}
            <Link to="/formats" className="font-semibold">
              {t('login.pickFormat')}
            </Link>
          </span>
          <LanguageSwitch />
        </div>
      </div>
    </form>
  );
}
